import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import './styles.css';

interface AIIconProps {
  size?: number;
  starScale?: number;
  className?: string;
  /** Si es false, el glow no sigue el cursor; se mantiene estático y centrado. Default: true */
  followMouse?: boolean;
}

export interface AIIconHandle {
  startThinking: () => void;
  stopThinking: () => void;
  startSpeaking: () => void;
  stopSpeaking: () => void;
  playInitialAnimation: () => void;
}

export const AIIcon = forwardRef<AIIconHandle, AIIconProps>(({ size = 120, starScale = 1, className = '', followMouse = true }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [intensity, setIntensity] = useState(0.25);
  const [isThinking, setIsThinking] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [isInitialAnimating, setIsInitialAnimating] = useState(false);
  const targetIntensityRef = useRef(0.25);

  useImperativeHandle(ref, () => ({
    startThinking: () => {
      setIsRecovering(false);
      setIsSpeaking(false);
      setIsThinking(true);
    },
    stopThinking: () => {
      setIsThinking(false);
      setIsRecovering(true);
    },
    startSpeaking: () => {
      setIsRecovering(false);
      setIsThinking(false);
      setIsSpeaking(true);
    },
    stopSpeaking: () => {
      setIsSpeaking(false);
      setIsRecovering(true);
    },
    playInitialAnimation: () => {
      setIsInitialAnimating(true);
      // Resetear después de la animación (3s total)
      setTimeout(() => {
        setIsInitialAnimating(false);
      }, 3000);
    },
  }));

  useEffect(() => {
    if (!followMouse) return; // Glow estático: no seguir el cursor
    if (isThinking || isSpeaking) return; // No seguir el mouse cuando está pensando o hablando

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const distX = e.clientX - centerX;
      const distY = e.clientY - centerY;
      const distance = Math.sqrt(distX * distX + distY * distY);
      
      const maxDistance = 800;
      const minIntensity = 0.05;
      const maxIntensity = 0.6;
      
      // Intensidad decrece con la distancia
      const normalizedDist = Math.min(distance / maxDistance, 1);
      const newIntensity = maxIntensity - (normalizedDist * (maxIntensity - minIntensity));
      
      // Guardar el target para el lerp de recovery
      targetIntensityRef.current = newIntensity;
      
      // Si no está recuperándose, aplicar intensidad directamente
      if (!isRecovering) {
        setIntensity(newIntensity);
      }
      
      // Siempre seguir el ratón, calculando la dirección
      const angle = Math.atan2(distY, distX);
      const maxOffset = 70; // Máximo desplazamiento desde el centro
      const offsetFactor = Math.min(distance / 200, 1); // Factor de cuánto se desplaza
      
      const x = 50 + (Math.cos(angle) * maxOffset * offsetFactor);
      const y = 50 + (Math.sin(angle) * maxOffset * offsetFactor);
      
      setMousePos({ x, y });
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, [isThinking, isSpeaking, isRecovering, followMouse]);

  // Efecto de recuperación suave con lerp cuando termina speaking/thinking
  useEffect(() => {
    if (!isRecovering) return;

    let animationId: number;
    let currentIntensity = intensityRef.current;
    
    const animateRecovery = () => {
      const targetIntensity = targetIntensityRef.current;
      const lerpSpeed = 0.08; // Velocidad del lerp (más bajo = más suave)
      
      currentIntensity = currentIntensity + (targetIntensity - currentIntensity) * lerpSpeed;
      setIntensity(currentIntensity);
      
      // Cuando esté suficientemente cerca del target, terminar recovery
      if (Math.abs(currentIntensity - targetIntensity) < 0.01) {
        setIntensity(targetIntensity);
        setIsRecovering(false);
        return;
      }
      
      animationId = requestAnimationFrame(animateRecovery);
    };

    animateRecovery();

    return () => cancelAnimationFrame(animationId);
  }, [isRecovering]);

  // Transición suave al centro cuando está pensando o hablando
  useEffect(() => {
    if (!isThinking && !isSpeaking) return;

    const targetX = 50;
    const targetY = 50;
    
    // Si la intensidad es baja, el glow ya se ve centrado visualmente,
    // así que reseteamos la posición inmediatamente sin animación
    if (intensityRef.current < 0.2) {
      setMousePos({ x: targetX, y: targetY });
      return;
    }
    
    const duration = 500; // ms
    const startTime = Date.now();
    const startX = mousePos.x;
    const startY = mousePos.y;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Easing function (ease-out)
      const eased = 1 - Math.pow(1 - progress, 3);

      const newX = startX + (targetX - startX) * eased;
      const newY = startY + (targetY - startY) * eased;

      setMousePos({ x: newX, y: newY });

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isThinking, isSpeaking]);

  // Usar ref para trackear intensidad actual sin re-renders
  const intensityRef = useRef(intensity);
  useEffect(() => {
    intensityRef.current = intensity;
  }, [intensity]);

  // Animación lerp de intensidad cuando está pensando
  useEffect(() => {
    if (!isThinking) return;

    let animationId: number;
    const thinkingMin = 0.3;
    const thinkingMax = 0.8;
    const speed = 0.03;
    
    // Capturar intensidad actual
    const startIntensity = intensityRef.current;
    let time = 0;
    const transitionDuration = 30; // frames para hacer la transición inicial
    let transitionFrame = 0;

    const animateIntensity = () => {
      time += speed;
      
      // Calcular el valor objetivo de la onda sinusoidal
      const wave = (Math.sin(time) + 1) / 2;
      const targetIntensity = thinkingMin + (thinkingMax - thinkingMin) * wave;
      
      // Durante los primeros frames, hacer lerp desde la intensidad inicial
      let newIntensity: number;
      if (transitionFrame < transitionDuration) {
        const t = transitionFrame / transitionDuration;
        const eased = 1 - Math.pow(1 - t, 2); // ease-out
        newIntensity = startIntensity + (targetIntensity - startIntensity) * eased;
        transitionFrame++;
      } else {
        newIntensity = targetIntensity;
      }
      
      setIntensity(newIntensity);
      animationId = requestAnimationFrame(animateIntensity);
    };

    animateIntensity();

    return () => cancelAnimationFrame(animationId);
  }, [isThinking]);

  // Animación de "hablar" - pulsos intensos y suaves simulando el habla
  useEffect(() => {
    if (!isSpeaking) return;

    let animationId: number;
    const speakingMin = 0.4;
    const speakingMax = 0.95;
    const startIntensity = intensityRef.current;
    let time = 0;
    const transitionDuration = 20; // frames para transición inicial
    let transitionFrame = 0;
    let previousIntensity = startIntensity;
    
    const animateSpeaking = () => {
      time += 0.08; // Velocidad de la onda
      
      // Onda principal con variaciones para simular habla
      const mainWave = Math.sin(time * 3) * 0.5 + 0.5; // Onda rápida
      const secondaryWave = Math.sin(time * 7) * 0.2; // Variación de alta frecuencia
      const breathWave = Math.sin(time * 0.5) * 0.1; // Respiración suave
      
      const combinedWave = Math.max(0, Math.min(1, mainWave + secondaryWave + breathWave));
      const targetIntensity = speakingMin + (speakingMax - speakingMin) * combinedWave;
      
      // Transición inicial suave
      let newIntensity: number;
      if (transitionFrame < transitionDuration) {
        const t = transitionFrame / transitionDuration;
        const eased = 1 - Math.pow(1 - t, 2);
        newIntensity = startIntensity + (targetIntensity - startIntensity) * eased;
        transitionFrame++;
      } else {
        // Lerp suave para transiciones fluidas
        const lerpSpeed = 0.2;
        newIntensity = previousIntensity + (targetIntensity - previousIntensity) * lerpSpeed;
      }
      
      previousIntensity = newIntensity;
      setIntensity(newIntensity);
      animationId = requestAnimationFrame(animateSpeaking);
    };

    animateSpeaking();

    return () => cancelAnimationFrame(animationId);
  }, [isSpeaking]);

  return (
    <div 
      ref={containerRef}
      className={`ai-icon ${className} ${isThinking ? 'ai-icon--thinking' : ''} ${isSpeaking ? 'ai-icon--speaking' : ''} ${isInitialAnimating ? 'ai-icon--initial-animation' : ''}`}
      style={{ 
        width: size, 
        height: size,
        '--mouse-x': `${mousePos.x}%`,
        '--mouse-y': `${mousePos.y}%`,
        '--intensidad': intensity,
      } as React.CSSProperties}
    >
      <svg 
        className="ai-icon__star" 
        style={{ width: size * starScale, height: size * starScale }}
        viewBox="0 0 77 74" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <g filter="url(#filter0_d_57_11173)">
          <path d="M32.33 20.54C35.08 16.22 41.39 16.22 44.14 20.54L46.33 23.99C46.88 24.85 47.61 25.58 48.48 26.13L51.92 28.33C56.24 31.08 56.24 37.39 51.92 40.14L48.48 42.33C47.61 42.88 46.88 43.61 46.33 44.48L44.14 47.92C41.39 52.24 35.08 52.24 32.33 47.92L30.13 44.48C29.58 43.61 28.85 42.88 27.99 42.33L24.54 40.14C20.22 37.39 20.22 31.08 24.54 28.33L27.99 26.13C28.85 25.58 29.58 24.85 30.13 23.99L32.33 20.54Z" fill="url(#paint0_linear_57_11173)"/>
          <path d="M32.75 20.81C35.3 16.8 41.16 16.8 43.71 20.81L45.91 24.25C46.5 25.18 47.28 25.97 48.21 26.56L51.65 28.75C55.67 31.3 55.67 37.16 51.65 39.71L48.21 41.91C47.28 42.5 46.5 43.28 45.91 44.21L43.71 47.65C41.16 51.67 35.3 51.67 32.75 47.65L30.56 44.21C29.97 43.28 29.18 42.5 28.25 41.91L24.81 39.71C20.8 37.16 20.8 31.3 24.81 28.75L28.25 26.56C29.18 25.97 29.97 25.18 30.56 24.25L32.75 20.81Z" stroke="url(#paint1_linear_57_11173)"/>
        </g>
        <defs>
          <filter id="filter0_d_57_11173" x="-1.14441e-05" y="-0.00019455" width="76.4623" height="76.4623" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dy="4"/>
            <feGaussianBlur stdDeviation="10.65"/>
            <feComposite in2="hardAlpha" operator="out"/>
            {/* El drop-shadow de color se aplica por CSS (.ai-icon__star) vía filtro
                drop-shadow con el color de marca, ya que un feColorMatrix nativo no
                puede leer variables CSS. Aquí usamos un drop neutro/transparente. */}
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0"/>
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_57_11173"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_57_11173" result="shape"/>
          </filter>
          {/* Los stop-color se pisan por CSS (ver styles.css) con el color de marca.
              Aquí se dejan los valores originales como fallback. */}
          <linearGradient id="paint0_linear_57_11173" x1="38.2311" y1="11.2622" x2="38.2624" y2="73.2622" gradientUnits="userSpaceOnUse">
            <stop className="ai-icon__grad0-start" stopColor="#FFFEFB"/>
            <stop className="ai-icon__grad0-end" offset="1" stopColor="#375DFB"/>
          </linearGradient>
          <linearGradient id="paint1_linear_57_11173" x1="38.2311" y1="57.1997" x2="38.2311" y2="11.2622" gradientUnits="userSpaceOnUse">
            <stop className="ai-icon__grad1-start" stopColor="#FFFEFB" stopOpacity="0.2"/>
            <stop className="ai-icon__grad1-end" offset="1" stopColor="#375DFB" stopOpacity="0.7"/>
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
});

AIIcon.displayName = 'AIIcon';
